<template>
  <b-modal
    :id="modalId"
    title="下載資料"
    ok-title="關閉"
    ok-only
    scrollable
    size="lg"
    centered
    @show="onShow"
    @hidden="onHidden"
  >
    <template #modal-title>
      <span>下載資料</span>
      <span v-if="selectedWorkerName || workerName" class="text-muted ml-1 font-weight-normal">（{{ selectedWorkerName || workerName }}）</span>
    </template>

    <b-overlay
      :show="loading"
      spinner-variant="primary"
      spinner-type="grow"
      rounded="sm"
    >
      <div v-if="!loading && groupedDocuments.length === 0" class="text-center text-muted py-4">
        此師傅在支出記錄中尚無上傳文件。
      </div>
      <div v-else class="worker-expense-docs-list">
        <div
          v-for="group in groupedDocuments"
          :key="group.monthKey"
          class="mb-4"
        >
          <h6 class="text-primary border-bottom pb-1 mb-2">{{ group.monthLabel }}</h6>
          <ul class="list-unstyled mb-0">
            <li
              v-for="doc in group.items"
              :key="doc.id"
              class="d-flex align-items-center py-2 border-bottom doc-row"
            >
              <a
                v-if="doc.expense_number"
                :href="expenditureEditHref(doc)"
                target="_blank"
                rel="noopener noreferrer"
                class="expenditure-number-link mr-2 text-nowrap"
                @click.stop
              >{{ doc.expense_number }}</a>
              <template v-else>
                <span class="mr-2 text-muted text-nowrap">—</span>
              </template>
              <a
                v-if="doc.is_image && doc.download_url"
                :href="doc.download_url"
                target="_blank"
                rel="noopener noreferrer"
                class="doc-thumb mr-2 flex-shrink-0"
                @click.stop
              >
                <img :src="doc.download_url" :alt="doc.file_name" class="doc-thumb-img" />
              </a>
              <feather-icon v-else icon="FileTextIcon" size="18" class="mr-2 text-muted flex-shrink-0" />
              <span class="flex-grow-1 text-break doc-file-name" @click="downloadFile(doc)">{{ doc.file_name }}</span>
              <feather-icon icon="DownloadIcon" size="18" class="text-primary cursor-pointer flex-shrink-0" @click="downloadFile(doc)" />
            </li>
          </ul>
        </div>
      </div>
    </b-overlay>
  </b-modal>
</template>

<script>
import axiosIns from '@/libs/axios';
import { BOverlay } from 'bootstrap-vue';

export default {
  name: 'ModalWorkerExpenseDocuments',
  components: {
    BOverlay,
  },
  props: {
    workerId: {
      type: [Number, String],
      default: null,
    },
    workerName: {
      type: String,
      default: '',
    },
    modalId: {
      type: String,
      default: 'modal-worker-expense-documents',
    },
  },
  data() {
    return {
      loading: false,
      documents: [],
      // 由 open(worker) 寫入，避免第一次打開時 props 尚未更新而打到錯的 API
      selectedWorkerId: null,
      selectedWorkerName: '',
    };
  },
  computed: {
    groupedDocuments() {
      const list = this.documents || [];
      const map = {};
      list.forEach((doc) => {
        const key = doc.month_key || '';
        const label = doc.month_label || '未分類';
        if (!map[key]) {
          map[key] = { monthKey: key, monthLabel: label, items: [] };
        }
        map[key].items.push(doc);
      });
      return Object.values(map).sort((a, b) => (b.monthKey || '').localeCompare(a.monthKey || ''));
    },
  },
  methods: {
    /** 由父層在打開 modal 前呼叫，傳入師傅資料，確保第一次打開就用正確 id 打 API */
    open(worker) {
      const id = worker && (worker.id !== undefined) ? worker.id : this.workerId;
      const name = (worker && (worker.name_cn || worker.name)) || this.workerName || '';
      this.selectedWorkerId = id;
      this.selectedWorkerName = name;
    },
    onShow() {
      const id = this.selectedWorkerId != null ? this.selectedWorkerId : this.workerId;
      if (id) {
        this.selectedWorkerId = id;
        this.selectedWorkerName = this.selectedWorkerName || this.workerName || '';
        this.loadDocuments(id);
      }
    },
    onHidden() {
      this.documents = [];
    },
    loadDocuments(workerId) {
      const id = workerId != null ? workerId : (this.selectedWorkerId != null ? this.selectedWorkerId : this.workerId);
      if (!id) return;
      this.loading = true;
      axiosIns
        .get(`workers/${id}/expense-documents`)
        .then((res) => {
          this.documents = res.data.documents || [];
        })
        .catch((err) => {
          this.$swal({
            text: (err.response && err.response.data && err.response.data.message) || '取得文件列表失敗',
            icon: 'error',
            confirmButtonText: '確定',
            customClass: { confirmButton: 'btn btn-primary' },
          });
          this.documents = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    expenditureEditHref(doc) {
      if (!doc.expense_id) return '#';
      const route = this.$router.resolve({ name: 'expenditure-edit', params: { id: doc.expense_id } });
      return route.href;
    },
    downloadFile(doc) {
      if (!doc.download_url) return;
      window.open(doc.download_url, '_blank', 'noopener,noreferrer');
    },
  },
};
</script>

<style lang="scss" scoped>
.doc-row {
  transition: background-color 0.15s;
  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
}
.expenditure-number-link {
  color: var(--primary);
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
}
.doc-thumb {
  display: block;
  line-height: 0;
}
.doc-thumb-img {
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #ddd;
}
.doc-file-name {
  cursor: pointer;
}
.cursor-pointer {
  cursor: pointer;
}
</style>
